import React, { useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { CurrentUser, User } from "@bitsentry-ce/components/types/api.types";
import { toast } from "sonner";
import type { AuthContextType, AuthProviderProps } from "./types";
import { AuthContext } from "./context";
import { useCurrentUser, useLogout, useUpdateProfile } from "../trpc/hooks";
import type { UserProfile } from "../trpc/hooks/auth.trpc";
import { clearTokens, setToken, getToken } from "./utils";
import { cacheUtils } from "../query/persist";
import { useTranslation } from "@bitsentry-ce/i18n";
import { captureAnalyticsEvent, syncAnalyticsUser } from "../../posthog";

function warnSentryFailure(action: string, error: unknown): void {
  console.warn(`[sentry] Failed to ${action}`, error);
}

function trackLoginSuccess(method: "workos"): void {
  captureAnalyticsEvent("auth_login_succeeded", {
    method,
  });
}

function clearUserObservabilityContext(): void {
  syncAnalyticsUser(null);
  try {
    Sentry.setUser(null);
  } catch (error: unknown) {
    warnSentryFailure("clear user context", error);
  }
}

type RawRelation = { id?: string | number; name?: string } | null | undefined;

const normalizeRelation = (
  relation: RawRelation,
): { id: number; name: string } | undefined => {
  if (relation == null) {
    return undefined;
  }

  const relId =
    typeof relation.id === "string"
      ? Number.parseInt(relation.id, 10)
      : (relation.id ?? null);

  if (relId == null || Number.isNaN(relId)) {
    return undefined;
  }

  return {
    id: relId,
    name: relation.name ?? "",
  };
};

function getRawUserId(
  raw: Partial<CurrentUser> & { id?: string | number },
): number | null {
  if (typeof raw.id === "string") {
    return Number.parseInt(raw.id, 10);
  }

  return typeof raw.id === "number" ? raw.id : null;
}

const normalizeUser = (data: unknown): CurrentUser | null => {
  if (data == null || typeof data !== "object") {
    return null;
  }

  const raw = data as Partial<CurrentUser> & { id?: string | number };
  const idValue = getRawUserId(raw);

  if (
    idValue == null ||
    Number.isNaN(idValue) ||
    typeof raw.email !== "string"
  ) {
    return null;
  }

  return {
    id: idValue,
    firstName: raw.firstName,
    lastName: raw.lastName,
    email: raw.email,
    role: normalizeRelation(raw.role as RawRelation),
    status: normalizeRelation((raw as { status?: RawRelation }).status),
    createdAt: raw.createdAt ?? new Date().toISOString(),
    updatedAt: raw.updatedAt ?? new Date().toISOString(),
  };
};

function isUnauthorizedError(error: unknown): boolean {
  if (error == null || typeof error !== "object") {
    return false;
  }

  if (
    !("data" in error) ||
    error.data == null ||
    typeof error.data !== "object"
  ) {
    return false;
  }

  if (!("code" in error.data)) {
    return false;
  }

  return error.data.code === "UNAUTHORIZED";
}

function toSentryUser(user: User | null): Parameters<typeof Sentry.setUser>[0] {
  if (user === null) {
    return null;
  }

  return {
    id: String(user.id),
    email: user.email,
    role: user.role?.name,
  };
}

function getErrorToastMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}

function getLogoutRedirectUrl(result: unknown): string | undefined {
  if (
    result == null ||
    typeof result !== "object" ||
    !("logoutUrl" in result)
  ) {
    return undefined;
  }

  const { logoutUrl } = result;
  if (typeof logoutUrl === "string" && logoutUrl !== "") {
    return logoutUrl;
  }

  return undefined;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const queryClient = useQueryClient();

  // Clear auth-related cache if no token exists on startup
  useEffect(() => {
    const token = getToken();
    if (token === null || token === "") {
      // Clear any stale auth cache when there's no token
      cacheUtils.clearAll();
    }
  }, []);

  const { data: currentUser, isLoading, error } = useCurrentUser();
  const logoutMutation = useLogout();
  const updateProfileMutation = useUpdateProfile();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isUnauthorizedError(error)) {
      clearTokens();
      clearUserObservabilityContext();
      setUser(null);
      setReady(true);
      return;
    }

    const transformedUser = normalizeUser(currentUser);
    setUser(transformedUser);
    setReady(true);
  }, [currentUser, error, isLoading]);

  useEffect(() => {
    if (!ready) {
      return;
    }

    syncAnalyticsUser(user);
    try {
      Sentry.setUser(toSentryUser(user));
    } catch (error: unknown) {
      warnSentryFailure("sync user context", error);
    }
  }, [ready, user]);

  // Set auth state synchronously after the WorkOS callback returns.
  const loginWithToken = useCallback(
    (
      token: string,
      user: unknown,
      remember = false,
      method: "workos" = "workos",
    ): void => {
      setToken(token, remember);
      const normalizedUser = normalizeUser(user);
      if (normalizedUser !== null) {
        setUser(normalizedUser);
      }
      setReady(true);
      trackLoginSuccess(method);
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    let logoutUrl: string | undefined;

    try {
      const result: unknown = await logoutMutation.mutateAsync();
      logoutUrl = getLogoutRedirectUrl(result);
    } finally {
      captureAnalyticsEvent("auth_logout");
      clearUserObservabilityContext();
      clearTokens();
      setUser(null);
      queryClient.clear();
      toast.success(t("common.provider.successfullyLoggedOut"));
      if (logoutUrl !== undefined && logoutUrl !== "") {
        window.location.href = logoutUrl;
      } else {
        window.location.href = "/login";
      }
    }
  }, [logoutMutation, queryClient, t]);

  const updateUser = useCallback(
    async (userData: Partial<CurrentUser>): Promise<void> => {
      try {
        if (userData.email !== undefined) {
          const error = new Error(
            "Email and authentication settings are managed by WorkOS in this environment. Update those fields through WorkOS instead.",
          );
          toast.error(error.message);
          throw error;
        }

        const payload: UserProfile = {
          firstName: userData.firstName,
          lastName: userData.lastName,
        };

        const updatedUser = normalizeUser(
          await updateProfileMutation.mutateAsync(payload),
        );

        if (updatedUser !== null) {
          setUser(updatedUser);
        }

        // Defer toast to avoid flushSync during render
        setTimeout(
          () => toast.success(t("common.provider.profileUpdatedSuccessfully")),
          0,
        );
      } catch (err) {
        toast.error(
          getErrorToastMessage(err, t("errors.profile.failedToUpdate")),
        );
        throw err;
      }
    },
    [updateProfileMutation, t],
  );

  const value: AuthContextType = {
    user,
    isAuthenticated: user !== null,
    isLoading: !ready,
    loginWithToken,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
